import { Combobox } from '@headlessui/react';
import { TextField } from '@schuettflix/react-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SearchOption } from '@/shared/components/SearchOption.tsx';
import { useSearchConstructionProjects } from '@/clients/constructionProject/ConstructionProject.ts';
import { ConstructionProjectSearchItem } from '@/clients/constructionProject/types/constructionProject.ts';

export interface SelectedConstructionProject {
    id: string;
    projectCode: string;
    organizationId: number;
    userId: number;
    name: string;
    organizationName: string;
}

interface ConstructionProjectSearchDropdownProps {
    value: SelectedConstructionProject | null;
    onChange: (project: SelectedConstructionProject | null) => void;
    disabled?: boolean;
}

function toSelectedProject(item: ConstructionProjectSearchItem): SelectedConstructionProject {
    return {
        id: item.id,
        projectCode: item.projectCode,
        organizationId: item.organizationInfo.id,
        userId: item.responsibleEmployee.id,
        name: item.name,
        organizationName: item.organizationInfo.name,
    };
}

export function ConstructionProjectSearchDropdown({
    value,
    onChange,
    disabled,
}: ConstructionProjectSearchDropdownProps) {
    const { searchConstructionProjects } = useSearchConstructionProjects();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<ConstructionProjectSearchItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (value) {
            setQuery(value.name);
        }
    }, [value]);

    const performSearch = useCallback(
        async (searchTerm: string) => {
            if (searchTerm.length < 2) {
                setResults([]);
                return;
            }

            setIsLoading(true);
            try {
                const response = await searchConstructionProjects(searchTerm);
                setResults(response.constructionProjects);
            } catch (error) {
                console.error('Failed to search construction projects:', error);
                setResults([]);
            } finally {
                setIsLoading(false);
            }
        },
        [searchConstructionProjects]
    );

    const handleQueryChange = useCallback(
        (newQuery: string) => {
            setQuery(newQuery);

            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }

            if (newQuery.length === 0) {
                onChange(null);
                setResults([]);
                return;
            }

            debounceRef.current = setTimeout(() => {
                void performSearch(newQuery);
            }, 300);
        },
        [onChange, performSearch]
    );

    const handleSelect = useCallback(
        (item: ConstructionProjectSearchItem | null) => {
            if (!item) {
                onChange(null);
                return;
            }

            setQuery(item.name);
            onChange(toSelectedProject(item));
        },
        [onChange]
    );

    // Cast to bypass TypeScript's strict typing for the `as` prop with generics
    const ComboboxDiv = Combobox as typeof Combobox & { (props: { as: 'div' } & Record<string, unknown>): JSX.Element };

    return (
        <ComboboxDiv
            as="div"
            value={null}
            onChange={(item: ConstructionProjectSearchItem | null) => handleSelect(item)}
            disabled={disabled}
        >
            {/* https://github.com/tailwindlabs/headlessui/discussions/1236#discussioncomment-2970969 */}
            <Combobox.Button as="div">
                <Combobox.Input
                    as={TextField}
                    value={query}
                    displayValue={() => query}
                    placeholder="Search construction projects..."
                    onChange={event => handleQueryChange(event.target.value)}
                />
            </Combobox.Button>
            <div className="relative">
                <Combobox.Options className="scroll font-copy-md bg-surface shadow-high absolute top-2 z-10 max-h-[250px] w-full min-w-[400px] overflow-y-auto rounded">
                    {isLoading ? (
                        <div className="p-4 text-center text-sm text-gray-500">Searching...</div>
                    ) : results.length === 0 && query.length >= 2 ? (
                        <Combobox.Option value={null} disabled>
                            <SearchOption active={false} selected={false} title="No results found" />
                        </Combobox.Option>
                    ) : (
                        results.map(project => (
                            <Combobox.Option key={project.id} value={project} title={project.name}>
                                {props => (
                                    <SearchOption
                                        {...props}
                                        title={project.name}
                                        side={project.projectCode}
                                        description={`${project.organizationInfo.name} (Org: ${project.organizationInfo.id})`}
                                    />
                                )}
                            </Combobox.Option>
                        ))
                    )}
                </Combobox.Options>
            </div>
        </ComboboxDiv>
    );
}
